import { MailOpen } from 'lucide-react';
import { Form } from '@remix-run/react';
import { useEffect, useState } from 'react';

const GoogleIcon: React.FC = () => {
  return (
    <div className="w-6 h-6 relative">
      <svg width="22" height="22" viewBox="0 0 24 24" fill="none">
        <path
          d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z"
          fill="#4285F4"
        />
        <path
          d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"
          fill="#34A853"
        />
        <path
          d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z"
          fill="#FBBC05"
        />
        <path
          d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z"
          fill="#EA4335"
        />
      </svg>
    </div>
  );
};

interface GoogleOnboardingProps {
  handleEmailSubmit: (e: React.FormEvent) => void;
  email: string;
  setEmail: (email: string) => void;
  isCheckingEmail: boolean;
  isBlocked: boolean;
}

export default function GoogleOnboarding({
  handleEmailSubmit,
  email,
  setEmail,
  isCheckingEmail,
  isBlocked,
}: GoogleOnboardingProps) {
  const [errorMessage, setErrorMessage] = useState('');

  useEffect(() => {
    if (isBlocked) {
      setErrorMessage('Disposable email addresses are not allowed.');

      const timer = setTimeout(() => setErrorMessage(''), 5000);
      return () => clearTimeout(timer);
    }

    return undefined;
  }, [isBlocked]);

  return (
    <div className="w-full font-inter-display">
      {/* Sign Up Form */}
      <div className="space-y-6">
        {/* Google Sign In */}
        <div className="space-y-2.5">
          <Form action="/auth/google" method="post">
            <button
              type="submit"
              className="w-full bg-white border border-[#E5E5E5] rounded-xl py-3 sm:py-3 px-4 flex items-center justify-center gap-2.5 hover:bg-[#F5F5F5] transition-colors min-h-[48px] sm:min-h-auto"
            >
              <GoogleIcon />
              <span className="text-black text-base font-medium leading-[1.44em]">Continue with Google</span>
            </button>
          </Form>

          {/* comment out email form for production push for the time being */}

          {/* Divider */}
          <div className="flex items-center gap-2 py-2">
            <div className="flex-1 h-px bg-[#6F6F6F]"></div>
            <span className="text-[#6F6F6F] text-xs font-medium leading-[1.2em]">or Sign in with Email</span>
            <div className="flex-1 h-px bg-[#6F6F6F]"></div>
          </div>
          {/* </div> */}

          {/* Email Form */}
          <div className="space-y-5">
            <form onSubmit={handleEmailSubmit} className="space-y-3">
              <div className="space-y-3">
                <label className="block text-white text-base text-md font-medium leading-[1.2em]">Email</label>
                <div className="relative">
                  <input
                    type="email"
                    placeholder="Joh@mail.com"
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    className="
                    w-full bg-transparent border border-[#424242] rounded-xl py-3 px-4 pr-12 text-white placeholder:[#5A5B60] placeholder:text-sm placeholder:opacity-50 text-base font-normal leading-[1.2em] focus:outline-none focus:border-[#555] transition-colors min-h-[48px] sm:min-h-auto
                  "
                  />
                  <div className="absolute right-4 top-1/2 transform -translate-y-1/2">
                    <MailOpen className="w-5 h-5 text-[#908B8B]" />
                  </div>
                </div>
              </div>

              {errorMessage && <p className="text-[#FF796E] text-sm font-normal leading-[1.2em]">{errorMessage}</p>}

              <div className="space-y-4">
                <button
                  type="submit"
                  disabled={!email}
                  className={`w-full rounded-xl py-3 sm:py-2.5 px-4 shadow-sm transition-colors min-h-[48px] sm:min-h-auto ${
                    email ? 'bg-[#F7F7F7] hover:bg-[#E5E5E5]' : 'bg-[#FDFDFD66] cursor-not-allowed'
                  }`}
                >
                  <span
                    className={`text-base font-medium leading-[1.5em] tracking-[0.02em] ${email ? 'text-black' : 'text-[#4C4D52]'}`}
                  >
                    {isCheckingEmail ? 'Sending Code...' : 'Send me a code'}
                  </span>
                </button>

                <p className="text-center text-[#696A71] text-xs font-normal leading-[1.2em]">
                  By proceeding you agree to Dualite's{' '}
                  <a
                    href="https://dualite.dev/legal/terms-of-use"
                    target="_blank"
                    rel="noopener noreferrer"
                    className="text-white"
                  >
                    Terms of Use
                  </a>
                </p>
              </div>
            </form>
          </div>
        </div>
      </div>
    </div>
  );
}
